import { BalanceArea, NameArea, WarnIfNotPosted } from "./patientSummaryItems";
import { Card, Result, Skeleton, theme } from "antd";
import { usePatientSummaryQuery } from "api/clients/usePatientSummaryQuery";
import { usePmsSummaryQuery } from "api/clients/usePmsSummaryQuery";
import type { NeedsMergePatient } from "../../components/PaymentsLanding/NewPaymentModal/MergeFlow/mergeFlow.props";
import type { PMSPatientSummaryResult, PatientSummaryResult } from "api/responses";

const { useToken } = theme;

interface PatientSummaryFromIdProps {
    /** The payments ID of the patient to display. */
    readonly id: number;
}

/** Display standard patient summary information by loading it using an ID. */
export const PatientSummaryFromId = ({ id }: PatientSummaryFromIdProps) => {
    const { isPending, isError, data } = usePatientSummaryQuery(id);

    return <PatientSummary isPending={isPending} isError={isError} patientData={data} />;
};

interface PatientSummaryFromPmsIdProps {
    /** The PMS ID of the patient. */
    readonly pmsId: string;

    /** Optional date that is part of Bulk Request eligibility. */
    readonly paidWithoutWriteBackDate?: number;

    /** When set, hide the already requested balance (because it is displayed somewhere else). */
    readonly hideAlreadyRequested?: boolean;
}

/** Display standard patient summary information by loading it using an ID. */
export const PatientSummaryFromPmsId = ({
    pmsId,
    paidWithoutWriteBackDate,
    hideAlreadyRequested,
}: PatientSummaryFromPmsIdProps) => {
    const { isPending, isError, data } = usePmsSummaryQuery(pmsId);

    return (
        <PatientSummary
            isPending={isPending}
            paidWithoutWriteBackDate={paidWithoutWriteBackDate}
            hideAlreadyRequested={hideAlreadyRequested}
            isError={isError}
            patientData={data}
        />
    );
};

interface PatientSummaryProps {
    /** Indicates if patient data is still loading. */
    readonly isPending?: boolean;

    /** Indicates if there was an error loading patient data. */
    readonly isError?: boolean;

    /** Optional date that is part of Bulk Request eligibility. */
    readonly paidWithoutWriteBackDate?: number;

    /** When set, hide the already requested balance (because it is displayed somewhere else). */
    readonly hideAlreadyRequested?: boolean;

    /** The summary information about a patient, whether PMS-integrated or not. */
    readonly patientData: PatientSummaryResult | PMSPatientSummaryResult | undefined;
}

/** Display standard patient summary information. */
export const PatientSummary = ({ isPending = false, isError = false, patientData, ...props }: PatientSummaryProps) => {
    const { token } = useToken();
    const integrated = isIntegratedPatient(patientData);

    // Forcing Border Color Here To Use Primary
    // Its using secondary because no title
    return (
        <Card
            className={`vyne-patient-summary ${!integrated ? "vyne-patient-summary-non-integrated" : "vyne-patient-summary-integrated"}`}
            style={{ borderColor: token.colorBorderSecondary }}
        >
            {isPending ? (
                <Skeleton active data-testid="patient-summary-loading-skeleton" />
            ) : isError ? (
                <Result status="error" title="Unable to retrieve patient details" />
            ) : patientData && integrated ? (
                <IntegratedPatientSummaryItems patientData={patientData} {...props} />
            ) : patientData && !integrated ? (
                <NonIntegratedPatientSummaryItems patientData={patientData} />
            ) : (
                <Result status="warning" title="No Patient Details Found" />
            )}
        </Card>
    );
};

interface integratedPatientSummaryItemsProps {
    readonly patientData: PMSPatientSummaryResult;

    readonly hideAlreadyRequested?: boolean;

    readonly paidWithoutWriteBackDate?: number;
}

const IntegratedPatientSummaryItems = ({
    patientData,
    paidWithoutWriteBackDate,
    hideAlreadyRequested,
}: integratedPatientSummaryItemsProps) => (
    <>
        <NameArea
            givenName={patientData.givenName}
            familyName={patientData.familyName}
            isGuarantor={patientData.isGuarantor}
            dateOfBirth={patientData.dateOfBirth}
            pmsBalances={patientData.pmsBalances}
            dependents={patientData.dependents}
            hasOutstandingClaims={patientData.hasOutstandingClaims}
        />
        <BalanceArea
            balance={patientData.balance}
            pmsBalances={patientData.pmsBalances}
            hideAlreadyRequested={hideAlreadyRequested}
        />
        <WarnIfNotPosted paidWithoutWriteBackDate={paidWithoutWriteBackDate} />
    </>
);

interface nonIntegratedPatientSummaryItemsProps {
    readonly patientData: PatientSummaryResult;
}

const NonIntegratedPatientSummaryItems = ({ patientData }: nonIntegratedPatientSummaryItemsProps) => (
    <>
        <NameArea givenName={patientData.givenName} familyName={patientData.familyName} hasOutstandingClaims={false} />
        {!patientData.isInsuranceCarrier && <BalanceArea balance={patientData.balance} />}
    </>
);

// Helpers
function isIntegratedPatient(
    patientData: PatientSummaryResult | PMSPatientSummaryResult | NeedsMergePatient | undefined,
): patientData is PMSPatientSummaryResult {
    // returns (Has PMS Balances and Is Not An Unlinked Patient)
    return (
        !!patientData &&
        "pmsBalances" in patientData &&
        !!patientData.pmsBalances &&
        !("unlinked" in patientData && patientData.unlinked)
    );
}
